import LabelledElement from "./LabelledElement";
import LayersSummary from "./LayersSummary";
import SubjectivitiesElement from "./SubjectivitiesElement";
import {
  Checkbox,
  FormControlLabel,
  FormLabel,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { formatDate } from "common/dates";
import { currencyFormat } from "common/numbers";
import { toSentenceCase } from "common/strings";
import { useSelector } from "react-redux";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";
import * as userSelectors from "store/selectors/user/userSelectors";

const SummaryElement = ({ form, items }) => {
  const hasIndustryClass = items.some((x) => x.format === "industryClass");
  const {
    data: industryClasses,
    isLoading: industryClassesLoading,
  } = claimsService.useIndustryClassesQuery(null, { skip: !hasIndustryClass });
  const getIndustryClassName = (industryClass) => {
    if (industryClassesLoading) {
      return null;
    }
    const name = (industryClasses ?? []).find((x) => x.key === industryClass)
      ?.name;
    return name ?? toSentenceCase(industryClass);
  };

  const hasExposureFlags = items.some((x) => x.exposureFlag != null);
  const {
    data: flags,
    isLoading: flagsLoading,
  } = staticDataService.useConfigQuery("flags", { skip: !hasExposureFlags });
  const getFlagName = (type, flag) => {
    if (flagsLoading) {
      return null;
    }
    const options = (flags?.defaultExposureFlags ?? []).find(
      (x) => x.type === type
    )?.categoryOptions;
    const name = (options ?? []).find((x) => x.key === flag)?.display;
    return name ?? toSentenceCase(flag);
  };

  const isFormsAdmin = useSelector(userSelectors.isFormsAdminUser);

  return (
    <Stack marginBottom={10}>
      {(items ?? []).map((item, index) => {
        const data = form?.[item.source ?? "formData"] ?? {};
        const value = data[item.key];

        if (item.format === "title") {
          return (
            <Typography
              key={index}
              variant={"h5"}
              sx={{ margin: "1rem 0 !important" }}
            >
              {item.label}
            </Typography>
          );
        } else if (item.format === "label") {
          return (
            <FormLabel
              key={index}
              sx={{
                marginBottom: "0.375rem",
                color: "rgba(0, 0, 0, 0.8)",
                fontWeight: "500",
              }}
            >
              {item.label}
            </FormLabel>
          );
        } else if (item.format === "checkbox") {
          return (
            <FormControlLabel
              key={index}
              control={<Checkbox checked={value} />}
              label={item.label}
              sx={{ pointerEvents: "none" }}
            />
          );
        } else if (item.format === "layers") {
          return (
            <LayersSummary
              key={index}
              data={data}
              quote={form?.quote ?? {}}
              boundLayerId={form?.boundLayerId}
            />
          );
        } else if (item.format === "subjectivities") {
          return (
            <LabelledElement label={item.label} key={index}>
              <SubjectivitiesElement data={data} />
              {(data?.subjectivities ?? []).length === 0 && (
                <Typography>{"-"}</Typography>
              )}
            </LabelledElement>
          );
        } else if (item.format === "producer") {
          return !isFormsAdmin ? (
            <></>
          ) : (
            <LabelledElement label={item.label} key={index}>
              <Typography>
                {value == null ? "-" : `${value?.name} (${value?.company})`}
              </Typography>
            </LabelledElement>
          );
        }

        let formatted = value;

        if (item.format === "currency") {
          formatted = currencyFormat(value);
        } else if (item.format === "date") {
          formatted = formatDate(value);
        } else if (item.format === "industryClass") {
          formatted = getIndustryClassName(value);
        } else if (item.format === "policyNumber") {
          formatted = form?.boundPolicyNumber;
        } else if (item.exposureFlag != null) {
          formatted = getFlagName(item.exposureFlag, value);
        }

        const showSkeleton =
          item.loading ||
          (item.format === "industryClass" && industryClassesLoading) ||
          (item.exposureFlag != null && flagsLoading);

        return (
          <LabelledElement label={item.label} key={index}>
            {showSkeleton ? (
              <Skeleton width={Math.min((value?.length || 5) * 8, 300)} />
            ) : (
              <Typography
                sx={{ whiteSpace: item.multiline ? "pre-line" : null }}
              >
                {formatted || "-"}
              </Typography>
            )}
          </LabelledElement>
        );
      })}
    </Stack>
  );
};

export default SummaryElement;
